import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Input,
  Button,
  Form,
  Space,
  Typography,
  Avatar,
  message,
  AutoComplete,
  Card,
  Row,
  Col,
  InputNumber,
  Collapse,
  Empty,
  Tag,
  Tooltip,
} from 'antd';
import {
  CarOutlined,
  SaveOutlined,
  EditOutlined,
  PlusOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { AnimatePresence } from 'framer-motion';
import { Vehicle } from '../types';
import { vehicleApi } from '../services/api';
import { getMakeSuggestions, getModelSuggestions } from '../data/vehicleData';
import { useEstimateFormContext } from '../contexts/EstimateFormContext';
import { AIFieldConfidenceBadge } from './AIFieldConfidenceBadge';
import type { VehicleMatch } from '../utils/vehicleMatcher';

const { Text } = Typography;
const { TextArea } = Input;

interface VehicleInlineFormProps {
  customerId?: number;
  customerName?: string;
  value?: Vehicle;
  onChange?: (vehicle: Vehicle) => void;
  disabled?: boolean;
  /** List of AI-matched vehicle candidates — shown as a recommendation list. */
  aiVehicleCandidates?: VehicleMatch[];
  /** Called when user picks a vehicle from the recommendation list. */
  onAcceptVehicleCandidate?: (vehicle: Vehicle) => void;
  /** Called when user dismisses the list (wants to add new vehicle). */
  onDismissVehicleCandidates?: () => void;
}

const VehicleInlineForm: React.FC<VehicleInlineFormProps> = ({
  customerId,
  customerName,
  value,
  onChange,
  disabled = false,
  aiVehicleCandidates = [],
  onAcceptVehicleCandidate,
  onDismissVehicleCandidates,
}) => {
  const [form] = Form.useForm();
  const formContext = useEstimateFormContext();
  const [isFormMounted, setIsFormMounted] = useState(false);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(value || null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedMake, setSelectedMake] = useState<string>('');
  const [vinDecoding, setVinDecoding] = useState(false);

  // No debounce needed — vehicle list is already loaded client-side, filter instantly

  // Guard: track the last vehicleFieldsApproved we applied so we don't
  // re-run form.setFieldsValue on every context re-render.
  const appliedAiDataRef = useRef<Record<string, string> | null>(null);

  // ── AI confidence helpers ──
  const fieldConf = (fieldName: string) =>
    formContext.fieldConfidences[`vehicle.${fieldName}`];

  const confStyle = (fieldName: string): React.CSSProperties => {
    const fc = fieldConf(fieldName);
    if (!fc) return {};
    const shadows: Record<string, string> = {
      blue: '0 0 0 2px rgba(82, 196, 26, 0.3)',
      yellow: '0 0 0 2px rgba(250, 173, 20, 0.4)',
      red: '0 0 0 2px rgba(255, 77, 79, 0.4)',
    };
    return { boxShadow: shadows[fc.band] ?? '' };
  };

  const confStatus = (fieldName: string): 'warning' | 'error' | undefined => {
    const fc = fieldConf(fieldName);
    if (!fc) return undefined;
    if (fc.band === 'yellow') return 'warning';
    if (fc.band === 'red') return 'error';
    return undefined;
  };

  // Auto-expand "More Options" when AI fills license_plate or notes
  const aiHasDetailFields = useMemo(() => {
    const detailFields = ['license_plate', 'notes'];
    return detailFields.some((f) => !!formContext.fieldConfidences[`vehicle.${f}`]);
  }, [formContext.fieldConfidences]);

  const collapseActiveKeys = aiHasDetailFields ? ['1'] : undefined;

  // Form lifecycle: register/unregister with context
  useEffect(() => {
    setIsFormMounted(true);
    formContext.registerVehicleForm();

    return () => {
      formContext.unregisterVehicleForm();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (customerId) {
      fetchVehicles(customerId);
    } else {
      setVehicles([]);
      setSelectedVehicle(null);
      setIsCreatingNew(false);
      setIsEditing(false);
    }
  }, [customerId]);

  // Effect to handle AI-approved vehicle data from context (replaces setTimeout-based prop approach)
  // Guard: only apply once per unique vehicleFieldsApproved reference to avoid re-running
  // form.setFieldsValue on every context re-render (e.g. when markFieldReviewed fires).
  // Also waits if a suggestion banner is showing — user must decide first.
  useEffect(() => {
    if (!isFormMounted || !formContext.vehicleFormReady) return;
    if (!customerId) return; // Vehicle depends on customer being selected
    // If a candidates list is showing, wait for user to pick or dismiss before filling the form
    if (aiVehicleCandidates.length > 0) return;

    const aiData = formContext.aiState.vehicleFieldsApproved;
    if (!aiData) return;

    // Skip if we already applied this exact data object
    if (appliedAiDataRef.current === aiData) return;
    appliedAiDataRef.current = aiData;

    // Show the form in create mode
    setIsCreatingNew(true);

    // Use requestAnimationFrame to ensure DOM is fully rendered before setting values
    requestAnimationFrame(() => {
      // Coerce numeric fields: InputNumber + { type: 'number' } validators require
      // actual numbers, but context stores everything as strings.
      const yearNum = aiData.year
        ? parseInt(String(aiData.year).replace(/,/g, ''), 10)
        : undefined;
      const mileageRaw = aiData.current_mileage || aiData.mileage;
      const mileageNum = mileageRaw
        ? parseInt(String(mileageRaw).replace(/,/g, ''), 10)
        : undefined;

      form.setFieldsValue({
        year: yearNum != null && !isNaN(yearNum) ? yearNum : '',
        make: aiData.make || '',
        model: aiData.model || '',
        vin: aiData.vin || '',
        license_plate: aiData.license_plate || '',
        color: aiData.color || '',
        current_mileage: mileageNum != null && !isNaN(mileageNum) ? mileageNum : '',
        notes: aiData.notes || '',
      });
      if (aiData.make) {
        setSelectedMake(aiData.make);
      }

      // Trigger form validation to show populated state
      form.validateFields().catch(() => {});
    });
  }, [isFormMounted, formContext.vehicleFormReady, formContext.aiState.vehicleFieldsApproved, customerId, aiVehicleCandidates, form]);

  // Auto-save trigger: when phase transitions to 'vehicle_save', automatically save
  useEffect(() => {
    if (formContext.aiState.orchestratorPhase === 'vehicle_save') {
      handleSave(false);
    }
  }, [formContext.aiState.orchestratorPhase]);

  useEffect(() => {
    if (value && value !== selectedVehicle) {
      setSelectedVehicle(value);
      setIsCreatingNew(false);
      setIsEditing(false);
      // Delay populateForm until after React has committed the state updates to the DOM.
      // Without this, form.setFieldsValue() fires before the Form.Item components
      // have re-rendered into "selected vehicle" mode, so the values don't stick.
      requestAnimationFrame(() => {
        populateForm(value);
      });
      // Mark current AI data as "already applied" so the AI fill effect
      // doesn't re-fire and overwrite the selected vehicle's data.
      const currentAiData = formContext.aiState.vehicleFieldsApproved;
      if (currentAiData) {
        appliedAiDataRef.current = currentAiData;
      }
    }
  }, [value]);

  const fetchVehicles = async (customerId: number) => {
    try {
      const response = await vehicleApi.getByCustomerId(customerId);
      setVehicles(response.data || []);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      message.error('Failed to fetch vehicles');
      setVehicles([]);
    }
  };

  const populateForm = (vehicle: Vehicle) => {
    form.setFieldsValue({
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      current_mileage: vehicle.current_mileage,
      vin: vehicle.vin,
      license_plate: vehicle.license_plate,
      color: vehicle.color,
      engine_size: vehicle.engine_size,
      notes: vehicle.notes,
    });
    setSelectedMake(vehicle.make || '');
    setHasChanges(false);
  };

  const handleSearch = (searchText: string) => {
    setSearchValue(searchText);
  };

  const handleSelect = (value: string, option: any) => {
    if (value === 'CREATE_NEW') {
      setIsCreatingNew(true);
      setSelectedVehicle(null);
      setIsEditing(false);
      form.resetFields();

      // Pre-populate from search if it looks like year make model
      const parts = searchValue.trim().split(' ');
      if (parts.length >= 3) {
        const year = parseInt(parts[0]);
        if (year > 1900 && year <= new Date().getFullYear() + 1) {
          form.setFieldsValue({
            year: year,
            make: parts[1],
            model: parts.slice(2).join(' '),
          });
        }
      }
      setHasChanges(false);
    } else {
      const vehicle = vehicles.find(v => v.id === parseInt(value));
      if (vehicle) {
        setSelectedVehicle(vehicle);
        setIsCreatingNew(false);
        setIsEditing(false);
        populateForm(vehicle);
        if (onChange) {
          onChange(vehicle);
        }
      }
    }
    setSearchValue('');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (selectedVehicle) {
      populateForm(selectedVehicle);
      setIsEditing(false);
    } else {
      setIsCreatingNew(false);
      form.resetFields();
    }
    setHasChanges(false);
  };

  const handleSave = async (updateVehicle: boolean = false) => {
    try {
      // Prevent duplicate submissions
      if (loading) {
        console.warn('Save already in progress, ignoring duplicate request');
        return;
      }

      const values = await form.validateFields();
      setLoading(true);

      const vehicleData: Omit<Vehicle, 'id' | 'created_at' | 'updated_at'> = {
        customer_id: customerId!,
        year: values.year,
        make: values.make,
        model: values.model,
        current_mileage: values.current_mileage,
        vin: values.vin || '',
        license_plate: values.license_plate || '',
        color: values.color || '',
        engine_size: values.engine_size || '',
        notes: values.notes || '',
      };

      let savedVehicle: Vehicle;

      if (isCreatingNew || !selectedVehicle) {
        // Create new vehicle via API
        const response = await vehicleApi.create(vehicleData);
        savedVehicle = response.data;
        message.success('Vehicle added successfully');

        // Refresh the vehicle list
        await fetchVehicles(customerId!);
      } else if (updateVehicle && selectedVehicle?.id) {
        // Update existing vehicle via API
        const response = await vehicleApi.update(selectedVehicle.id, vehicleData);
        savedVehicle = response.data;
        message.success('Vehicle updated successfully');

        // Refresh the vehicle list
        await fetchVehicles(customerId!);
      } else {
        savedVehicle = selectedVehicle!;
      }

      setSelectedVehicle(savedVehicle);
      setIsCreatingNew(false);
      setIsEditing(false);
      setHasChanges(false);

      // Notify parent via prop callback
      if (onChange) {
        onChange(savedVehicle);
      }

      // Notify context for phase transition (completes the flow)
      formContext.onVehicleSaved(savedVehicle);
    } catch (error) {
      message.error('Please fill in all required fields');
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = () => {
    setHasChanges(true);
  };

  const handleVinLookup = async () => {
    const vinValue = form.getFieldValue('vin');
    if (!vinValue || vinValue.length !== 17) {
      message.error('Please enter a valid 17-character VIN before decoding.');
      return;
    }

    setVinDecoding(true);
    try {
      const response = await vehicleApi.vinLookup(vinValue);
      const decoded = response.data.decoded;

      const patch: Record<string, any> = {};
      if (decoded.year) patch.year = decoded.year;
      if (decoded.make) patch.make = decoded.make;
      if (decoded.model) patch.model = decoded.model;
      if (decoded.engine_size) patch.engine_size = decoded.engine_size;
      if (decoded.body_class) patch.body_class = decoded.body_class;
      if (decoded.drive_type) patch.drive_type = decoded.drive_type;
      if (decoded.transmission_type) patch.transmission_type = decoded.transmission_type;
      if (decoded.fuel_type) patch.fuel_type = decoded.fuel_type;
      if (decoded.turbo != null) patch.turbo = decoded.turbo;
      form.setFieldsValue(patch);
      if (decoded.make) setSelectedMake(decoded.make);
      setHasChanges(true);

      message.success('VIN decoded — year, make, model & specs auto-filled.');
    } catch (err: any) {
      const detail = err?.response?.data?.error || err?.message || 'VIN decode failed';
      message.error(detail);
    } finally {
      setVinDecoding(false);
    }
  };

  // Get autocomplete options for make and model
  const makeOptions = getMakeSuggestions().map(make => ({ value: make }));
  const modelOptions = selectedMake ? getModelSuggestions(selectedMake).map(model => ({ value: model })) : [];

  const getFilteredVehicles = () => {
    // Filter instantly on searchValue — list is already loaded client-side
    if (!searchValue.trim()) return vehicles;

    const searchLower = searchValue.toLowerCase();
    return vehicles.filter(vehicle =>
      `${vehicle.year} ${vehicle.make} ${vehicle.model}`.toLowerCase().includes(searchLower) ||
      (vehicle.license_plate && vehicle.license_plate.toLowerCase().includes(searchLower)) ||
      (vehicle.vin && vehicle.vin.toLowerCase().includes(searchLower))
    );
  };

  const vehicleSearchOptions = useMemo(() => {
    if (!customerId) return [];

    const filtered = getFilteredVehicles();
    const opts = filtered.map(vehicle => ({
      value: vehicle.id!.toString(),
      label: (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0' }}>
          <Avatar
            size={24}
            style={{
              backgroundColor: '#f59e0b',
              color: 'white',
              fontSize: '10px'
            }}
            icon={<CarOutlined />}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '500', color: '#111827' }}>
              {vehicle.year} {vehicle.make} {vehicle.model}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {vehicle.current_mileage ? vehicle.current_mileage.toLocaleString() + ' miles' : ''}
              {vehicle.license_plate && ` • ${vehicle.license_plate}`}
            </div>
          </div>
        </div>
      ),
    }));

    if (vehicles.length === 0 || searchValue.trim()) {
      opts.push({
        value: 'CREATE_NEW',
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0' }}>
            <PlusOutlined style={{ color: '#f59e0b' }} />
            <span style={{ color: '#f59e0b' }}>
              {searchValue.trim() ? `Add vehicle: "${searchValue}"` : 'Add new vehicle'}
            </span>
          </div>
        ),
      });
    }

    return opts;
  }, [searchValue, vehicles, customerId]);

  const getVehicleDisplay = (vehicle: Vehicle) => {
    return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
  };

  const showForm = isCreatingNew || isEditing || selectedVehicle;

  if (!customerId) {
    return (
      <Card size="small" style={{ textAlign: 'center', color: '#6b7280' }}>
        <Text type="secondary">Please select a customer first</Text>
      </Card>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Search/Select Vehicle */}
      <div style={{ marginBottom: '16px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <AutoComplete
          value={searchValue}
          options={vehicleSearchOptions}
          onSearch={handleSearch}
          onSelect={handleSelect}
          placeholder={`Search vehicles for ${customerName || 'customer'} or add new...`}
          style={{ flex: 1 }}
          size="large"
          disabled={disabled}
          filterOption={false}
          open={searchValue.trim().length > 0 ? true : undefined}
        />
        <Button
          type="dashed"
          size="large"
          icon={<PlusOutlined />}
          onClick={() => { setIsCreatingNew(true); setSelectedVehicle(null); setIsEditing(false); form.resetFields(); setHasChanges(false); }}
          disabled={disabled}
          style={{ flexShrink: 0 }}
        >
          New Vehicle
        </Button>
      </div>

      {/* AI Recommendation List — existing vehicles found */}
      {aiVehicleCandidates.length > 0 && !selectedVehicle && (
        <Card
          size="small"
          style={{
            marginBottom: '12px',
            background: 'linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
          }}
        >
          <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <CarOutlined style={{ color: '#d97706', fontSize: '13px' }} />
            <Text strong style={{ color: '#92400e', fontSize: '13px' }}>
              Existing vehicles found
            </Text>
            <Tag color="orange" style={{ fontSize: '10px', margin: 0, marginLeft: 'auto', borderRadius: '8px' }}>
              {aiVehicleCandidates.length} match{aiVehicleCandidates.length !== 1 ? 'es' : ''}
            </Tag>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {aiVehicleCandidates.map((candidate, idx) => (
              <div
                key={candidate.vehicle.id || idx}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 10px',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  borderRadius: '6px',
                  border: '1px solid #fef3c7',
                }}
              >
                <Avatar
                  size={32}
                  style={{
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    fontSize: '11px',
                    flexShrink: 0,
                  }}
                  icon={<CarOutlined />}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: '#1f2937' }}>
                    {candidate.vehicle.year} {candidate.vehicle.make} {candidate.vehicle.model}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    {candidate.vehicle.current_mileage?.toLocaleString()} miles
                    {candidate.vehicle.license_plate && ` \u2022 ${candidate.vehicle.license_plate}`}
                    {candidate.vehicle.color && ` \u2022 ${candidate.vehicle.color}`}
                  </div>
                  <div style={{ display: 'flex', gap: '4px', marginTop: '2px' }}>
                    {candidate.matchReasons.map((reason, rIdx) => (
                      <Tag
                        key={rIdx}
                        color={candidate.matchConfidence >= 0.9 ? 'success' : 'default'}
                        style={{ fontSize: '10px', margin: 0, borderRadius: '8px', lineHeight: '16px' }}
                      >
                        {reason}
                      </Tag>
                    ))}
                  </div>
                </div>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => onAcceptVehicleCandidate?.(candidate.vehicle)}
                  style={{
                    fontSize: '11px',
                    borderRadius: '6px',
                    flexShrink: 0,
                    backgroundColor: '#f59e0b',
                    borderColor: '#f59e0b',
                  }}
                >
                  Use
                </Button>
              </div>
            ))}
          </div>

          {/* Add new vehicle instead */}
          <Button
            type="dashed"
            block
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              onDismissVehicleCandidates?.();
              setIsCreatingNew(true);
            }}
            style={{
              marginTop: '8px',
              fontSize: '12px',
              borderRadius: '6px',
              color: '#d97706',
              borderColor: '#fbbf24',
            }}
          >
            Add new vehicle instead
          </Button>
        </Card>
      )}

      {/* Selected Vehicle Display */}
      {selectedVehicle && !isEditing && !isCreatingNew && (
        <Card
          size="small"
          style={{
            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
            border: '1px solid #fbbf24',
            borderRadius: '8px',
            marginBottom: '16px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Avatar
                style={{
                  backgroundColor: '#f59e0b',
                  color: 'white'
                }}
                icon={<CarOutlined />}
              />
              <div>
                <Text strong style={{ color: '#92400e' }}>
                  {getVehicleDisplay(selectedVehicle)}
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  {selectedVehicle.current_mileage.toLocaleString()} miles
                  {selectedVehicle.license_plate && ` • ${selectedVehicle.license_plate}`}
                  {selectedVehicle.color && ` • ${selectedVehicle.color}`}
                </Text>
              </div>
            </div>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={handleEdit}
              disabled={disabled}
            >
              Edit
            </Button>
          </div>
        </Card>
      )}

      {/* Vehicle Form */}
      {showForm && (
        <Card
          title={
            isCreatingNew
              ? "New Vehicle Information"
              : isEditing
                ? "Edit Vehicle Information"
                : "Vehicle Information"
          }
          size="small"
          style={{ marginBottom: '16px' }}
          extra={
            (isCreatingNew || isEditing) && (
              <Space>
                <Button size="small" onClick={handleCancel} disabled={loading}>
                  Cancel
                </Button>
                {isEditing && hasChanges && (
                  <Button
                    type="default"
                    size="small"
                    onClick={() => handleSave(false)}
                    loading={loading}
                  >
                    Use Changes
                  </Button>
                )}
                <Button
                  type="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleSave(isEditing)}
                  loading={loading}
                >
                  {isCreatingNew ? 'Add Vehicle' : isEditing ? 'Update Vehicle' : 'Save'}
                </Button>
              </Space>
            )
          }
        >
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleFieldChange}
            disabled={disabled || (!!selectedVehicle && !isEditing && !isCreatingNew)}
            initialValues={{
              year: new Date().getFullYear(),
            }}
          >
            {/* VIN with Decode button — first for quick lookup */}
            <Form.Item
              label={
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  VIN
                  <AnimatePresence>{fieldConf('vin') && <AIFieldConfidenceBadge confidence={fieldConf('vin')!.confidence} band={fieldConf('vin')!.band} />}</AnimatePresence>
                  {(isCreatingNew || isEditing) && (
                    <Tooltip title="Auto-fill year, make, model & engine from NHTSA database">
                      <Button
                        type="link"
                        size="small"
                        icon={<ThunderboltOutlined />}
                        loading={vinDecoding}
                        onClick={handleVinLookup}
                        style={{ padding: '0 4px', height: 'auto', fontSize: 11, color: '#6366f1' }}
                      >
                        {vinDecoding ? 'Decoding...' : 'Decode VIN'}
                      </Button>
                    </Tooltip>
                  )}
                </span>
              }
              name="vin"
            >
              <Input
                placeholder="1HGBH41JXMN109186"
                maxLength={17}
                style={{
                  minWidth: '200px',
                  fontFamily: 'monospace',
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  ...confStyle('vin'),
                }}
                status={confStatus('vin')}
                showCount
                onChange={(e) => {
                  form.setFieldsValue({ vin: e.target.value.toUpperCase() });
                }}
                onFocus={() => {
                  formContext.markFieldReviewed('vehicle.vin');
                  formContext.setFocusedField({ path: 'vehicle.vin', current_value: form.getFieldValue('vin') });
                }}
                onBlur={() => formContext.setFocusedField(null)}
              />
            </Form.Item>

            {/* Year / Make / Model */}
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span>Year<AnimatePresence>{fieldConf('year') && <AIFieldConfidenceBadge confidence={fieldConf('year')!.confidence} band={fieldConf('year')!.band} />}</AnimatePresence></span>}
                  name="year"
                  rules={[
                    { required: true, message: 'Year is required' },
                    { type: 'number', min: 1900, max: new Date().getFullYear() + 1, message: 'Enter valid year' }
                  ]}
                >
                  <InputNumber
                    placeholder="2024"
                    style={{ width: '100%', minWidth: '80px', ...confStyle('year') }}
                    min={1900}
                    max={new Date().getFullYear() + 1}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.year');
                      formContext.setFocusedField({ path: 'vehicle.year', current_value: form.getFieldValue('year') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span>Make<AnimatePresence>{fieldConf('make') && <AIFieldConfidenceBadge confidence={fieldConf('make')!.confidence} band={fieldConf('make')!.band} />}</AnimatePresence></span>}
                  name="make"
                  rules={[{ required: true, message: 'Make is required' }]}
                >
                  <AutoComplete
                    placeholder="Select or type make"
                    options={makeOptions}
                    filterOption={(inputValue, option) =>
                      option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                    }
                    onChange={(value) => {
                      setSelectedMake(value);
                      form.setFieldsValue({ model: '' });
                    }}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.make');
                      formContext.setFocusedField({ path: 'vehicle.make', current_value: form.getFieldValue('make') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                    style={{ minWidth: '100px', ...confStyle('make') }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span>Model<AnimatePresence>{fieldConf('model') && <AIFieldConfidenceBadge confidence={fieldConf('model')!.confidence} band={fieldConf('model')!.band} />}</AnimatePresence></span>}
                  name="model"
                  rules={[{ required: true, message: 'Model is required' }]}
                >
                  <AutoComplete
                    placeholder={selectedMake ? "Select or type model" : "Select make first"}
                    options={modelOptions}
                    filterOption={(inputValue, option) =>
                      option!.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1
                    }
                    disabled={!selectedMake}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.model');
                      formContext.setFocusedField({ path: 'vehicle.model', current_value: form.getFieldValue('model') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                    style={{ minWidth: '100px', ...confStyle('model') }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Engine / Transmission / Color */}
            <Row gutter={16}>
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span>Engine<AnimatePresence>{fieldConf('engine_size') && <AIFieldConfidenceBadge confidence={fieldConf('engine_size')!.confidence} band={fieldConf('engine_size')!.band} />}</AnimatePresence></span>}
                  name="engine_size"
                >
                  <Input
                    placeholder="2.5L"
                    style={{ minWidth: '80px', ...confStyle('engine_size') }}
                    status={confStatus('engine_size')}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.engine_size');
                      formContext.setFocusedField({ path: 'vehicle.engine_size', current_value: form.getFieldValue('engine_size') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Transmission"
                  name="transmission_type"
                >
                  <Input
                    placeholder="Automatic"
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.transmission_type');
                      formContext.setFocusedField({ path: 'vehicle.transmission_type', current_value: form.getFieldValue('transmission_type') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label={<span>Color<AnimatePresence>{fieldConf('color') && <AIFieldConfidenceBadge confidence={fieldConf('color')!.confidence} band={fieldConf('color')!.band} />}</AnimatePresence></span>}
                  name="color"
                >
                  <Input
                    placeholder="Silver"
                    style={{ minWidth: '100px', ...confStyle('color') }}
                    status={confStatus('color')}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.color');
                      formContext.setFocusedField({ path: 'vehicle.color', current_value: form.getFieldValue('color') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Mileage */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label={<span>Current Mileage<AnimatePresence>{fieldConf('current_mileage') && <AIFieldConfidenceBadge confidence={fieldConf('current_mileage')!.confidence} band={fieldConf('current_mileage')!.band} />}</AnimatePresence></span>}
                  name="current_mileage"
                  rules={[
                    { required: true, message: 'Current mileage is required' },
                    { type: 'number', min: 0, message: 'Mileage must be positive' }
                  ]}
                >
                  <InputNumber
                    placeholder="50,000"
                    style={{ width: '100%', minWidth: '120px', ...confStyle('current_mileage') }}
                    min={0}
                    formatter={value => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
                    onFocus={() => {
                      formContext.markFieldReviewed('vehicle.current_mileage');
                      formContext.setFocusedField({ path: 'vehicle.current_mileage', current_value: form.getFieldValue('current_mileage') });
                    }}
                    onBlur={() => formContext.setFocusedField(null)}
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Collapsible More Options — License Plate & Notes */}
            <Collapse
              ghost
              size="small"
              activeKey={collapseActiveKeys}
              items={[
                {
                  key: '1',
                  label: 'More Options',
                  children: (
                    <>
                      <Form.Item
                        label={<span>License Plate<AnimatePresence>{fieldConf('license_plate') && <AIFieldConfidenceBadge confidence={fieldConf('license_plate')!.confidence} band={fieldConf('license_plate')!.band} />}</AnimatePresence></span>}
                        name="license_plate"
                      >
                        <Input
                          placeholder="ABC123"
                          style={{ minWidth: '100px', ...confStyle('license_plate') }}
                          status={confStatus('license_plate')}
                          onFocus={() => {
                            formContext.markFieldReviewed('vehicle.license_plate');
                            formContext.setFocusedField({ path: 'vehicle.license_plate', current_value: form.getFieldValue('license_plate') });
                          }}
                          onBlur={() => formContext.setFocusedField(null)}
                        />
                      </Form.Item>

                      <Form.Item
                        label="Notes"
                        name="notes"
                      >
                        <TextArea
                          placeholder="Vehicle notes, known issues, customer preferences..."
                          rows={2}
                          onFocus={() => {
                            formContext.markFieldReviewed('vehicle.notes');
                            formContext.setFocusedField({ path: 'vehicle.notes', current_value: form.getFieldValue('notes') });
                          }}
                          onBlur={() => formContext.setFocusedField(null)}
                        />
                      </Form.Item>
                    </>
                  ),
                }
              ]}
            />
          </Form>
        </Card>
      )}

      {/* Empty State */}
      {!selectedVehicle && !isCreatingNew && vehicles.length === 0 && (
        <Card size="small" style={{ textAlign: 'center' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No vehicles found for ${customerName || 'this customer'}`}
            style={{ margin: 0 }}
          >
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreatingNew(true)}
              disabled={disabled}
            >
              Add First Vehicle
            </Button>
          </Empty>
        </Card>
      )}

      {/* Helper Text */}
      {!selectedVehicle && !isCreatingNew && vehicles.length > 0 && (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Start typing to search vehicles or add a new one
        </Text>
      )}
    </div>
  );
};

export default VehicleInlineForm;
